/**
 * First-party upsell funnel reporting (owner 2026-08-16).
 *
 * `reportUpsell(action, surface, target)` does BOTH halves of tracking:
 *   1. the existing GA4/Clarity event via track() (upsell_shown etc.), and
 *   2. a batched beacon to the Worker's POST /projects/:id/upsell-events,
 *      which is the half our own report page can actually query. Accepted
 *      events are resolved against the final cart at order time on the
 *      Worker, so "accepted then removed" is distinguishable from revenue.
 *
 * Fire-and-forget by contract: batching (600ms), keepalive fetch, flush on
 * tab-hide/pagehide, and every failure swallowed — tracking must never break
 * ordering.
 *
 * The project handle is injected via setUpsellProjectProvider to keep this
 * module store-free. AUDIT B2 (2026-08-26): that provider used to be armed by
 * SizePaperScreen's effect, whose cleanup nulled it, so the whole mechanism
 * went dead the moment the customer left step 2 — every cart_smallprints
 * beacon was discarded (zero rows since 16 Aug) along with anything queued in
 * the last batching window of step 2. The registration now lives at module
 * scope in store/store.ts, whose lifetime is the bundle rather than a screen.
 */
import { track } from './analytics';

export type UpsellAction = 'shown' | 'accepted' | 'dismissed' | 'backed_out';

type Handle = { id: string; token: string } | null;
let provider: () => Handle = () => null;

export function setUpsellProjectProvider(fn: () => Handle): void {
  provider = fn;
}

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/apps/print/api';

/** Batching window, and the Worker's own per-request cap (projects.ts). */
const BATCH_MS = 600;
const BATCH_MAX = 20;
/* Events are HELD, not dropped, while the project handle is missing (B2), so
   bound what a never-resolving bootstrap can accumulate. 60 is far more than
   one session ever produces — it exists only so this cannot grow without end. */
const HOLD_MAX = 60;

let queue: Array<{ surface: string; action: UpsellAction; target?: string }> = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const handle = provider();
  /* B2: this used to `queue = []` whenever the handle was missing — a silent
     data loss that made "surface never fired" and "surface never recorded"
     look identical. Hold the events for the next flush instead; the beacon is
     allowed to be late, it is not allowed to lie. */
  if (!handle) {
    if (queue.length > HOLD_MAX) queue = queue.slice(-HOLD_MAX);
    return;
  }
  const events = queue.splice(0, BATCH_MAX);
  try {
    void fetch(`${BASE}/projects/${handle.id}/upsell-events`, {
      method: 'POST',
      keepalive: true, // survives navigation to checkout
      headers: {
        'Content-Type': 'application/json',
        'X-RPL-Project-Token': handle.token,
      },
      body: JSON.stringify({ events }),
    }).catch(() => undefined);
  } catch {
    /* never break the app for analytics */
  }
  // A held-back backlog can exceed one payload — drain the rest next tick.
  if (queue.length > 0 && !timer) timer = setTimeout(flush, BATCH_MS);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  /* B2: the cart surfaces beacon and then hand off to Shopify
     (window.location = '/checkout'), which can beat the 600ms batch. Chrome
     fires visibilitychange on a same-tab navigation but Safari/Firefox are
     not reliable about it; pagehide is the one event the whole unload path
     guarantees, and flush() is idempotent so firing both is harmless. */
  if (typeof window !== 'undefined') window.addEventListener('pagehide', () => flush());
}

export function reportUpsell(action: UpsellAction, surface: string, target?: string): void {
  // GA4/Clarity keeps its historical event names (upsell_shown / _accepted);
  // `dismissed` is new with the first-party funnel, `backed_out` with B4.
  track(`upsell_${action}`, { surface, ...(target ? { to: target } : {}) });
  queue.push({ surface, action, ...(target ? { target } : {}) });
  if (queue.length >= BATCH_MAX) flush();
  else if (!timer) timer = setTimeout(flush, BATCH_MS);
}
